<template>
	<DataTable
		:value="users"
		:loading="loading"
		class="p-datatable-sm p-datatable-documents"
		:autoLayout="true"
		columnResizeMode="fit"
		v-model:selection="selection"
		:selectionMode="selectionMode"
		@row-select="onRowSelect"
		:paginator="true"
		:rows="tableListPagination.pageSize"
		paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
		:rowsPerPageOptions="[25,50,100]"
		:currentPageReportTemplate="'{first} ' + $t('Squeeze.General.To') + ' {last} ' + $t('Squeeze.General.Of') + ' {totalRecords}  '"
		:scrollable="true"
		scrollHeight="flex"
		:rowHover="true"
		@page="onChangePage"
		:removableSort="true"
		filterDisplay="row"
		v-model:filters="filters"
		@filter="onFilter($event)"
	>
		<template #loading>
			{{  $t('Squeeze.General.Loading') }}
		</template>
		<template #empty>
			{{  $t('Squeeze.General.NoEntriesFound', { entryName: $t('Squeeze.UserManagement.User') }) }}
		</template>

		<Column field="id" header="ID" class="id-column" filterField="id" :sortable="true" :showFilterMenu="false" style="min-width: 4rem; max-width: 4rem;">
			<template #body="slotProps">
				{{slotProps.data.id}}
			</template>
			<template #filter="{filterModel, filterCallback}">
				<InputText style="min-width: 3rem;" class="p-column-filter filter-field" type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" />
			</template>
		</Column>
		<Column field="login" filterField="login" :header="$t('Squeeze.Login.Login')" :sortable="true" :showFilterMenu="false" style="min-width: 7rem;">
			<template #body="slotProps">
				<div class="column-cell" v-tooltip.bottom="slotProps.data.login">
					{{slotProps.data.login}}
				</div>
			</template>
			<template #filter="{filterModel, filterCallback}">
				<InputText style="min-width: 7rem;" class="p-column-filter filter-field" type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" />
			</template>
		</Column>
		<Column field="firstName" :header="$t('Squeeze.UserManagement.Firstname')" :sortable="true" :showFilterMenu="false" style="min-width: 7rem;">
			<template #body="slotProps">
				<div class="column-cell" v-tooltip.bottom="slotProps.data.firstName">
					{{slotProps.data.firstName}}
				</div>
			</template>
			<template #filter="{filterModel, filterCallback}">
				<InputText style="min-width: 7rem;" class="p-column-filter filter-field" type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" />
			</template>
		</Column>
		<Column field="lastName" :header="$t('Squeeze.UserManagement.Lastname')" :sortable="true" :showFilterMenu="false" style="min-width: 7rem;">
			<template #body="slotProps">
				<div class="column-cell" v-tooltip.bottom="slotProps.data.lastName">
					{{slotProps.data.lastName}}
				</div>
			</template>
			<template #filter="{filterModel, filterCallback}">
				<InputText style="min-width: 7rem;" class="p-column-filter filter-field" type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" />
			</template>
		</Column>
		<Column field="email" :header="$t('Squeeze.UserManagement.Email')" :sortable="true" :showFilterMenu="false" style="min-width: 10rem;" >
			<template #body="slotProps">
				<div class="column-cell" v-tooltip.bottom="slotProps.data.email">
					{{slotProps.data.email}}
				</div>
			</template>
			<template #filter="{filterModel, filterCallback}">
				<InputText style="min-width: 10rem;" class="p-column-filter filter-field" type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" />
			</template>
		</Column>
		<Column field="inheritedRoles" :header="''" :sortable="false" style="min-width: 4rem; max-width: 6rem;">
			<template #body="user">
				<template v-if="user.data.inheritedRoles.length > 0">
					{{ $t('Squeeze.UserManagement.Inherited') }}
				</template>
			</template>
		</Column>
		<Column field="externalUser" :header="''" :sortable="false" style="min-width: 3rem; max-width: 3rem; text-align: center;">
			<template #body="user">
				<div v-tooltip.bottom="$t('Squeeze.UserManagement.OAuth')">
					<i v-if="user.data.externalUser" class="mdi mdi-account-star-outline"></i>
				</div>
			</template>
		</Column>
		<!-- Delete & Edit -->
		<Column class="buttonColumn" style="min-width: 6rem; max-width: 6rem;">
			<template #body="user">
				<Button
					v-if="user.data.id !== 1"
					type="button"
					icon="mdi mdi-account-key-outline"
					v-tooltip.bottom="$t('Squeeze.UserManagement.TakeOverRoles')"
					class="p-button-lg p-button-rounded p-button-text"
					@click="openTakOverRolesDialog(user.data.id)"
				/>
				<Button
					:class="{ invisible: user.data.isInherited }"
					icon="mdi mdi-delete-outline"
					v-tooltip.bottom="$t('Squeeze.Validation.Buttons.Delete')"
					class="p-button-lg p-button-rounded p-button-danger p-button-text"
					@click="openDeleteDialog(user.data)"
				/>
			</template>
		</Column>

		<template #paginatorstart>
			<Button :label="$t('Squeeze.General.NewEntry')" type="button" icon="mdi mdi-plus" class="p-button" @click="onNewEntryClick" />
			<Button v-if="showBtnClearFilter" :label="$t('Squeeze.General.ClearFilters')" type="button" icon="mdi mdi-filter-off-outline" class="p-button p-ml-2" @click="clearFilters"/>
		</template>
		<template #paginatorend></template>
	</DataTable>

	<EntryDialog
		:show="showTakeOverRolesDialog"
		@onClose="showTakeOverRolesDialog = false"
		@onConfirm="executeTakeOverRoles"
		:loading="loadingTakeOverRoles"
		:headerText="headerTextOfTakeOverRoles"
		:showKeepDialogOpen="false"
	>
		<template #content>
			<UserTakeOverRolesView :userId="usersTakeOverRoles.userIdTo" :usersTakeOverRoles="usersTakeOverRoles" :showErrorMessage="showValidationMessage" @update="onUpdate"  />
		</template>
	</EntryDialog>
</template>

<script lang="ts">
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import {defineComponent, onMounted, PropType, reactive, ref} from "vue";
import {User} from "@dex/squeeze-client-ts";
import {useSqueezeStore} from "@/apps/squeeze/store";
import Tooltip from "primevue/tooltip";
import {ClientManager} from "@/singletons/ClientManager";
import {ToastManager} from "@/util/ToastManager";
import {useI18n} from "vue-i18n";
import {useToast} from "primevue/usetoast";
import EntryDialog from "@/components/EntryDialog.vue";
import UserTakeOverRolesView from "@/apps/administration/views/squeeze/users/tabs/UserTakeOverRolesView.vue";
import {TableListSettings, TableSettings} from "@/util/TableSettings";
import {useRoute} from "vue-router";
import {DocumentFilterObject} from "@/apps/squeeze/interfaces/DocumentSearch";
import {FilterMatchMode} from "primevue/api";
import InputText from "primevue/inputtext";

export default defineComponent({
	name: "UserList",
	components: {
		InputText,
		DataTable,
		Column,
		EntryDialog,
		UserTakeOverRolesView,
	},
	props: {
		users: {
			type: Array as PropType<User[]>,
		},
		loading: {
			type: Boolean,
			default: false,
		},
		selectionMode: {
			type: String,
			default: null,
		},
		showEditButton: {
			type: Boolean,
			default: false,
		},
	},
	directives: {
		'tooltip': Tooltip,
	},
	emits: [
		"onClickDelete", "onEntrySelect", "onRowSelect",
	],
	setup(props, {emit}) {
		const {t} = useI18n();
		const toast = useToast();
		const route = useRoute();

		/** Vuex Store */
		const store = useSqueezeStore();

		/** Current table list pagination */
		const tableListPagination = reactive<TableListSettings>({});

		/** Currently-Selected row */
		const selection = ref(null);

		/** Show the loading for Save-Button of take over roles? */
		const loadingTakeOverRoles = ref(false);

		/** Should the Entry-Dialog for take over roles be shown? */
		const showTakeOverRolesDialog = ref<boolean>(false);

		/** Text of the header in take over roles Entry-Dialog */
		const headerTextOfTakeOverRoles = ref<string>(t('Squeeze.UserManagement.TakeOverRoles'));

		/** Is the Form invalid? */
		const isValidationInvalid = ref(true);

		/** Show error validation-messages in form? */
		const showValidationMessage = ref(false);

		/** Object with current users for take over roles */
		const usersTakeOverRoles = reactive<any>({
			userIdFrom: null,
			userIdTo: null,
		});

		/** Filters of list (Currently not activated) */
		const filters = ref<DocumentFilterObject>({});

		/** Should the clear filter button be shown in the table? */
		const showBtnClearFilter = ref<boolean>(false);

		/** Service for getting the user-data */
		const userService = ClientManager.getInstance().squeeze.user;

		/**
		 * Opens the Delete-Dialog
		 * @param row The row to delete
		 */
		const openDeleteDialog = (row: User) => {
			emit("onClickDelete", row);
		}

		/** Triggered when the New Entry Button is clicked */
		const onNewEntryClick = () => {
			emit("onEntrySelect", {
				id: undefined,
				login: '',
				firstName: '',
				lastName: '',
				email: '',
				password: '',
			})
		}

		/**
		 * Triggered when the New Entry Button is clicked
		 * @param row The row to edit
		 */
		const openEditDialog = (row: User) => {
			emit("onEntrySelect", row)
		}

		/** Triggered when a row is selected */
		const onRowSelect = (event: any) => {
			emit("onRowSelect", event.data)
		}

		/**
		 * Opens the take over Roles Dialog
		 * @param userId
		 */
		const openTakOverRolesDialog = (userId: number) => {
			usersTakeOverRoles.userIdTo = userId;
			showTakeOverRolesDialog.value = true;
		}

		/** Triggered on update of attribute-form */
		const onUpdate = (data: any, isInvalid: boolean) => {
			isValidationInvalid.value = isInvalid;
			Object.assign(usersTakeOverRoles, data);
		}

		/**
		 * Execute the take over roles
		 * @param keepDialogOpen
		 */
		const executeTakeOverRoles = (keepDialogOpen: boolean = false) => {
			if (isValidationInvalid.value) {
				showValidationMessage.value = true;
				return;
			}
			showValidationMessage.value = false;
			loadingTakeOverRoles.value = true;

			userService.transferUserRoles(usersTakeOverRoles.userIdFrom, usersTakeOverRoles.userIdTo)
				.then(() => {
					if (!keepDialogOpen) {
						showTakeOverRolesDialog.value = false;
					}
					else {
						isValidationInvalid.value = true;
					}
					// Empty value
					usersTakeOverRoles.userIdFrom = null;
					ToastManager.showSuccess(toast, t('Squeeze.General.Success'), t('Squeeze.UserManagement.SuccessRolesTransfer'));
				}).catch(response => response.json().then ((err: { message: string }) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + err.message);
				})).finally(() => {
					loadingTakeOverRoles.value = false;
				})
		}

		onMounted(() => {
			tableListPagination.pageSize = TableSettings.getTableListPagination(store, route.name);
		});

		/** Triggered when page is changed */
		const onChangePage = (event: { rows: number }) => {
			tableListPagination.pageSize = event.rows;
			TableSettings.saveTableListPagination(t, toast, store, userService, route.name, event.rows);
		}

		/** Init filters */
		const initFilters = () => {
			filters.value = {
				"id": {value: null, matchMode: FilterMatchMode.EQUALS},
				"login": {value: null, matchMode: FilterMatchMode.CONTAINS},
				"firstName": {value: null, matchMode: FilterMatchMode.CONTAINS},
				"lastName": {value: null, matchMode: FilterMatchMode.CONTAINS},
				"email": {value: null, matchMode: FilterMatchMode.CONTAINS},
			};
		}

		/**
		 * Triggered when a filter has been entered
		 * @param event
		 */
		const onFilter = (event: any) => {
			showBtnClearFilter.value = event.filteredValue.length < props.users!.length;
		}

		/** Clear filters */
		const clearFilters = () => {
			initFilters();
		}

		initFilters();

		return {
			t,
			toast,
			store,
			tableListPagination,
			selection,
			loadingTakeOverRoles,
			showTakeOverRolesDialog,
			headerTextOfTakeOverRoles,
			isValidationInvalid,
			showValidationMessage,
			usersTakeOverRoles,
			filters,
			showBtnClearFilter,
			openDeleteDialog,
			onNewEntryClick,
			onRowSelect,
			openEditDialog,
			openTakOverRolesDialog,
			onUpdate,
			executeTakeOverRoles,
			onChangePage,
			onFilter,
			clearFilters,
		}
	},
});

</script>

<style lang="scss" scoped>

.invisible {
	visibility: hidden;
}

::v-deep(td div.column-cell) {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

// Table header
.p-datatable.p-datatable-documents ::v-deep(.p-datatable-thead tr th) {
	background-color: var(--dex-primary-light-color);
	padding: 0.6rem 0.6rem 0.2rem 0.3rem;
	border-bottom-color: var(--dex-primary-light-color);
	color: var(--dex-text-color);
}

.p-datatable.p-datatable-documents ::v-deep(.p-datatable-thead tr th:first-child) {
	padding: 0.6rem 0.6rem 0.2rem 0.6rem;
}

.p-datatable.p-datatable-documents ::v-deep(.p-datatable-thead .p-sortable-column-icon) {
	color: var(--dex-text-color);

	&:hover {
		color: var(--dex-text-color);
	}
}

.p-datatable.p-datatable-documents ::v-deep(.p-datatable-thead .p-inputtext.filter-field) {
	padding: 0.4rem;
	background-color: transparent;
	color: var(--dex-text-color);
	border-radius: 0;
	border-color: var(--filter-field-color);
	border-style: solid;
	border-width: 0.063rem;

	&:focus {
		border-color: rgba(255, 255, 255, 0.8);
	}
}

</style>

